import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface SessionExpiryModalProps {
  expiresAt: number; // Unix timestamp in seconds
  onExtend: () => void;
  onSignOut: () => void;
}

export default function SessionExpiryModal({ expiresAt, onExtend, onSignOut }: SessionExpiryModalProps) {
  const { t } = useTranslation();
  const [timeRemaining, setTimeRemaining] = useState(0);
  
  useEffect(() => {
    const updateTimer = () => {
      const now = Date.now() / 1000; // Convert to seconds
      const remaining = Math.max(0, expiresAt - now);
      setTimeRemaining(remaining);
    };
    
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    
    return () => clearInterval(interval);
  }, [expiresAt]);
  
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-dash-bg-surface rounded-dash-md border border-dash-border-subtle max-w-md w-full p-6">
        <div className="flex items-center justify-center mb-4">
          <div className="bg-dash-warning-bg rounded-full p-3">
            <AlertTriangle className="w-8 h-8 text-dash-warning-text" />
          </div>
        </div>
        
        <h2 className="text-xl font-bold text-center mb-2 text-dash-text-primary">{t('sessionExpiry.title')}</h2>

        <p className="text-dash-text-secondary text-center mb-6">
          {t('sessionExpiry.willExpireIn')}
        </p>
        
        <div className="flex items-center justify-center mb-6">
          <div className="bg-dash-bg-elevated rounded-dash-md px-6 py-3">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-dash-text-secondary" />
              <span className="text-2xl font-mono font-bold text-dash-text-primary">
                {formatTime(timeRemaining)}
              </span>
            </div>
          </div>
        </div>
        
        <p className="text-sm text-dash-text-secondary text-center mb-6">
          {t('sessionExpiry.instructions')}
        </p>

        <div className="flex gap-3">
          <button
            onClick={onSignOut}
            className="flex-1 px-4 py-2 bg-dash-bg-elevated text-dash-text-primary border border-dash-border hover:bg-dash-bg-hover rounded-dash-md transition-colors"
          >
            {t('sessionExpiry.signOutNow')}
          </button>
          <button
            onClick={onExtend}
            className="flex-1 px-4 py-2 bg-dash-accent text-dash-text-inverse rounded-dash-md hover:bg-dash-accent-hover transition-colors font-medium"
          >
            {t('sessionExpiry.staySignedIn')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Hook to manage session expiry warnings
export function useSessionExpiryWarning() {
  const { session, refreshSession, signOut } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [warningDismissed, setWarningDismissed] = useState(false);
  
  useEffect(() => {
    if (!session || warningDismissed) return;
    
    const checkExpiry = () => {
      const expiresAt = session.expires_at;
      if (!expiresAt) return;
      
      const now = Date.now() / 1000;
      const timeUntilExpiry = expiresAt - now;
      
      // Show warning when less than 5 minutes remaining
      if (timeUntilExpiry > 0 && timeUntilExpiry < 5 * 60) {
        setShowWarning(true);
      } else {
        setShowWarning(false);
      }
    };
    
    checkExpiry();
    const interval = setInterval(checkExpiry, 10000); // Check every 10 seconds
    
    return () => clearInterval(interval);
  }, [session, warningDismissed]);
  
  const handleExtend = async () => {
    try {
      await refreshSession();
      setShowWarning(false);
      setWarningDismissed(true);
      
      // Reset the dismissed flag after 30 minutes
      setTimeout(() => {
        setWarningDismissed(false);
      }, 30 * 60 * 1000);
    } catch (error) {
      console.error('Failed to extend session:', error);
    }
  };
  
  const handleSignOut = async () => {
    await signOut();
    setShowWarning(false);
  };
  
  return {
    showWarning: showWarning && session?.expires_at ? true : false,
    expiresAt: session?.expires_at || 0,
    handleExtend,
    handleSignOut
  };
}